import { useState } from "react";
import { Send } from "lucide-react";
import { useSmsRefreshStore } from "@/store/sms/sms-refresh.store";
import { SMS_MAX_CHARS, nonGsmChars } from "@/utils/sms";
import { useSendSms } from "./use-send-sms";

type Props = {
  leadId: number;
  firstName: string;
};

/**
 * One-line text box for a lead: Enter sends. The send, its idempotency key and its errors are the
 * SMS composer's (`useSendSms`); the lead's other SMS screens refresh after every attempt.
 */
const SmsQuickSend = ({ leadId, firstName }: Props) => {
  const [text, setText] = useState("");
  const { send, sending, error, clearError, resetKey } = useSendSms(leadId);
  const bump = useSmsRefreshStore((s) => s.bump);

  const blocked = !!error?.blocked;
  const overLimit = text.length > SMS_MAX_CHARS;
  const unsupported = nonGsmChars(text);
  const canSend = !!text.trim() && !overLimit && !sending && !blocked && unsupported.length === 0;

  const handleChange = (value: string) => {
    setText(value);
    // A different message deserves its own idempotency key.
    resetKey();
    if (error && !error.blocked) clearError();
  };

  const handleSend = async () => {
    if (!canSend) return;
    const draft = text;
    const sent = await send(draft.trim());
    // Even a failed attempt can leave an unconfirmed text on the thread.
    bump(leadId);
    // Keep a follow-up typed while this one was sending.
    if (sent) setText((current) => (current === draft ? "" : current));
  };

  if (blocked) return <p className="mt-2 text-[11px] text-accent-red">{error?.message}</p>;

  const hint = unsupported.length
    ? `Emojis and special characters can't be texted right now — remove ${unsupported.slice(0, 5).join(" ")} to send.`
    : overLimit
      ? `Too long: texts can be up to ${SMS_MAX_CHARS.toLocaleString()} characters.`
      : error?.message;

  return (
    <div>
      {hint && (
        <p role="alert" className="mt-2 text-[11px] text-accent-red">
          {hint}
        </p>
      )}
      <div className="mt-2 flex items-center gap-1.5">
        <input
          value={text}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.nativeEvent.isComposing) {
              e.preventDefault();
              e.stopPropagation();
              void handleSend();
            }
          }}
          aria-label={`Text ${firstName}`}
          placeholder={`Text ${firstName}…`}
          className="h-8 flex-1 rounded-lg border border-white/[0.08] bg-white/[0.02] px-2.5 text-[12px] text-white placeholder:text-slate-500 focus:border-brand-500/50 focus:outline-none"
        />
        <button
          type="button"
          onClick={() => void handleSend()}
          disabled={!canSend}
          aria-label="Send text"
          className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-brand-500 text-white transition hover:bg-brand-400 disabled:opacity-40"
        >
          <Send className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

export default SmsQuickSend;
